import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';

/**
 * Compact Data Mapping Card Component
 * Displays concise database mapping information for parameters
 *
 * @param {Object} dataMapping - Data mapping object from parameter
 */
const DataMappingCard = ({ dataMapping }) => {
  const [copiedText, setCopiedText] = useState('');

  if (!dataMapping || !dataMapping.tables || dataMapping.tables.length === 0) {
    return null;
  }

  // Get icon for table type
  const getTableTypeIcon = (type) => {
    return type === 'definition' ? <DescriptionIcon fontSize="small" /> : <TimelineIcon fontSize="small" />;
  };

  // Get color for table type
  const getTableTypeColor = (type) => {
    return type === 'definition' ? '#2196f3' : '#4caf50';
  };

  // Copy to clipboard handler
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  return (
    <Box sx={{
      p: 2,
      backgroundColor: '#fafafa',
      borderRadius: 1,
      border: '1px solid #e0e0e0'
    }}>
      {/* Compact Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
          Datenbank-Mapping
        </Typography>
        <Chip
          label={dataMapping.system}
          icon={<StorageIcon />}
          color="info"
          size="small"
          sx={{ height: '22px', fontSize: '0.75rem' }}
        />
      </Box>

      {/* Tables - Compact List */}
      {dataMapping.tables.map((table, index) => (
        <Box
          key={index}
          sx={{
            mb: index < dataMapping.tables.length - 1 ? 1.5 : 0,
            pl: 1.5,
            borderLeft: `3px solid ${getTableTypeColor(table.type)}`,
          }}
        >
          {/* Table Header Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            {getTableTypeIcon(table.type)}
            <Typography variant="caption" fontWeight="bold" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
              {table.type === 'definition' ? 'Definition' : 'Messwerte'}
            </Typography>
            <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace', ml: 0.5 }}>
              {table.name}
            </Typography>
            <Tooltip title={copiedText === table.name ? 'Kopiert!' : 'Tabellenname kopieren'}>
              <IconButton
                size="small"
                onClick={() => handleCopy(table.name, table.name)}
                sx={{ ml: 0.5, padding: '2px' }}
              >
                <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Description */}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
            {table.description}
          </Typography>

          {/* Columns - Comma-separated */}
          {table.columns && table.columns.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
              {table.columns.join(', ')}
            </Typography>
          )}

          {/* Join Condition - Inline */}
          {table.joinCondition && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <LinkIcon sx={{ fontSize: '0.8rem', color: '#f57c00' }} />
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#e65100' }}>
                {table.joinCondition}
              </Typography>
              <Tooltip title={copiedText === table.joinCondition ? 'Kopiert!' : 'Join kopieren'}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(table.joinCondition, table.joinCondition)}
                  sx={{ padding: '2px' }}
                >
                  <ContentCopyIcon sx={{ fontSize: '0.8rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default DataMappingCard;
